import { isMfaError, staffErrorMessage } from "@/components/admin/StaffAccessNotice";
import { createFileRoute, Link } from "@tanstack/react-router";
import { safeFilterTerm } from "@/lib/postgrest-safe";
import { queryOptions, useSuspenseQuery, useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { Suspense, useMemo, useState } from "react";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { requireSupabaseAuth } from "@/lib/require-auth";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { SiteHeader } from "@/components/site/SiteHeader";
import { AdminNav } from "@/components/admin/AdminNav";
import {
  adminCancelBooking,
  assignBookingProvider,
  exportBookingsCSV,
  listProvidersLite,
  retryBokunReservation,
  getVoucherStatuses,
  resendBookingVoucher,
} from "@/lib/booking-ops.functions";
import { resendSecureAccess } from "@/lib/booking-access-admin.functions";


const listAllBookings = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z
      .object({ status: z.string().optional(), search: z.string().max(120).optional() })
      .parse(d ?? {}),
  )
  .handler(async ({ context, data }) => {
    // NOTE: un owner puede tener varias filas (owner + admin), así que no se
    // puede usar .maybeSingle() aquí — devolvería error de "multiple rows".
    const { data: roleRows, error: roleError } = await context.supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", context.userId)
      .in("role", ["admin", "owner"])
      .limit(1);
    if (roleError) throw new Error(roleError.message);
    if (!roleRows || roleRows.length === 0) throw new Error("Forbidden");

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    let q = supabaseAdmin
      .from("custom_bookings")
      .select(
        "id, booking_code, tour_title, customer_name, customer_email, travel_date, travelers, currency, total, status, source, bokun_confirmation_code, notes, paid_at, created_at, provider_id, commission_pct",
      )
      .order("created_at", { ascending: false })
      .limit(200);
    // "attention": paid (or stuck reserving) with no Bokun reservation yet —
    // money taken and reservation missing, needs a retry or a manual refund.
    if (data.status === "attention") {
      q = q.in("status", ["paid", "reserving"]).is("bokun_confirmation_code", null).eq("source", "bokun");
    } else if (data.status && data.status !== "all") q = q.eq("status", data.status);

    const s = data.search ? safeFilterTerm(data.search) : "";
    if (s) {
      q = q.or(
        `booking_code.ilike.%${s}%,customer_email.ilike.%${s}%,customer_name.ilike.%${s}%,tour_title.ilike.%${s}%`,
      );
    }
    const { data: rows, error } = await q;
    if (error) throw new Error(error.message);
    return { bookings: rows ?? [] };
  });

const allBookingsQuery = (status: string, search: string) =>
  queryOptions({
    queryKey: ["admin", "bookings", status, search],
    queryFn: () => listAllBookings({ data: { status, search } }),
    staleTime: 15_000,
  });

export const Route = createFileRoute("/_authenticated/admin/bookings/")({
  head: () => ({ meta: [{ title: "Reservas — Admin" }, { name: "robots", content: "noindex,nofollow" }] }),
  component: AdminBookings,
});

function AdminBookings() {
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");

  function handleExport() {
    exportBookingsCSV({ data: {} }).then((res) => {
      const blob = new Blob([res.csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `reservas-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    }).catch((e) => {
      const msg = staffErrorMessage(e);
      if (isMfaError(e) && window.confirm(`${msg}\n\n¿Ir a la verificación en dos pasos?`)) {
        window.location.href = "/seguridad/mfa";
      } else if (!isMfaError(e)) {
        window.alert(msg);
      }
    });
  }

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-7xl space-y-6 px-4 py-10 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="font-display text-4xl font-semibold">Reservas</h1>
            <p className="mt-1 text-sm text-muted-foreground">Todas las reservas (Bokun y propias).</p>
          </div>
          <Button variant="outline" size="sm" onClick={handleExport}>
            Exportar CSV
          </Button>
        </div>
        <AdminNav />

        <div className="flex flex-wrap items-center gap-2">
          {["all", "attention", "pending", "paid", "reserving", "confirmed", "failed", "cancelled", "refunded", "completed"].map((s) => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              className={`rounded-full border px-3 py-1 text-xs ${
                status === s
                  ? "border-primary bg-primary/10 text-primary"
                  : s === "attention"
                    ? "border-destructive/40 text-destructive"
                    : "text-muted-foreground"
              }`}
            >
              {s === "attention" ? "⚠ requiere acción" : s}
            </button>
          ))}

          <Input
            className="ml-auto h-9 max-w-sm"
            placeholder="Buscar código, email, tour…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <TestAccessResend />
        <Suspense fallback={<Skeleton className="h-96 w-full" />}>
          <BookingsTable status={status} search={search} />
        </Suspense>
      </div>
    </div>
  );
}

function BookingsTable({ status, search }: { status: string; search: string }) {
  const q = useMemo(() => allBookingsQuery(status, search), [status, search]);
  const { data } = useSuspenseQuery(q);
  const qc = useQueryClient();
  const providersQ = useQuery({
    queryKey: ["admin", "providers", "lite"],
    queryFn: () => listProvidersLite(),
    staleTime: 60_000,
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ["admin", "bookings"] });

  const cancelMut = useMutation({
    mutationFn: (vars: { bookingId: string; reason: string }) =>
      adminCancelBooking({ data: vars }),
    onSuccess: invalidate,
  });
  const assignMut = useMutation({
    mutationFn: (vars: { bookingId: string; providerId: string | null; commissionPct: number | null }) =>
      assignBookingProvider({ data: vars }),
    onSuccess: invalidate,
  });
  const retryMut = useMutation({
    mutationFn: (vars: { bookingId: string }) => retryBokunReservation({ data: vars }),
    onSuccess: (res) => {
      invalidate();
      alert(res.ok ? "Reserva confirmada en Bokun." : `No se pudo reservar: ${res.error}`);
    },
  });

  const bookingIds = useMemo(() => data.bookings.map((b: any) => b.id), [data.bookings]);
  const vouchersQ = useQuery({
    queryKey: ["admin", "bookings", "vouchers", bookingIds],
    queryFn: () => getVoucherStatuses({ data: { bookingIds } }),
    enabled: bookingIds.length > 0,
    staleTime: 15_000,
  });
  const voucherMut = useMutation({
    mutationFn: (vars: { bookingId: string }) => resendBookingVoucher({ data: vars }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["admin", "bookings", "vouchers"] });
      alert(res.ok ? `Voucher enviado a ${res.recipient}.` : `No se pudo enviar: ${res.error}`);
    },
  });
  const voucherStatuses: Record<string, { status: string; error: string | null; attempt: number }> =
    (vouchersQ.data?.statuses as any) ?? {};



  if (!data.bookings.length) {
    return <p className="rounded-lg border bg-card p-8 text-center text-sm text-muted-foreground">Sin resultados.</p>;
  }


  const providers = providersQ.data?.providers ?? [];

  return (
    <div className="overflow-x-auto rounded-lg border bg-card">
      <table className="w-full min-w-[1100px] text-sm">
        <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
          <tr>
            <th className="px-3 py-2">Código</th>
            <th className="px-3 py-2">Tour</th>
            <th className="px-3 py-2">Cliente</th>
            <th className="px-3 py-2">Fecha</th>
            <th className="px-3 py-2">Pax</th>
            <th className="px-3 py-2">Total</th>
            <th className="px-3 py-2">Estado</th>
            <th className="px-3 py-2">Voucher</th>
            <th className="px-3 py-2">Proveedor</th>
            <th className="px-3 py-2">Comisión</th>
            <th className="px-3 py-2"></th>
          </tr>
        </thead>
        <tbody>
          {data.bookings.map((b: any) => (
            <tr key={b.id} className="border-t hover:bg-muted/30">
              <td className="px-3 py-2 font-mono text-xs">
                <Link to="/booking/$code" params={{ code: b.booking_code }} className="text-primary hover:underline">
                  {b.booking_code}
                </Link>
              </td>
              <td className="px-3 py-2">{b.tour_title}</td>
              <td className="px-3 py-2">
                <div className="font-medium">{b.customer_name}</div>
                <div className="text-xs text-muted-foreground">{b.customer_email}</div>
              </td>
              <td className="px-3 py-2">{b.travel_date ?? "—"}</td>
              <td className="px-3 py-2">{b.travelers}</td>
              <td className="px-3 py-2 whitespace-nowrap">
                {Number(b.total).toFixed(2)} {b.currency}
              </td>
              <td className="px-3 py-2">
                <StatusBadge status={b.status} />
                {b.source === "bokun" && !b.bokun_confirmation_code && b.status === "paid" && (
                  <div className="mt-1 text-[10px] font-semibold text-destructive">sin reserva Bokun</div>
                )}
                {b.notes && (
                  <div className="mt-1 max-w-[220px] text-[10px] text-muted-foreground" title={b.notes}>
                    {b.notes.length > 60 ? `${b.notes.slice(0, 60)}…` : b.notes}
                  </div>
                )}
              </td>

              <td className="px-3 py-2">
                <VoucherCell
                  state={voucherStatuses[b.id]}
                  pending={voucherMut.isPending}
                  onResend={() => {
                    if (confirm(`¿Reenviar el voucher de ${b.booking_code} a ${b.customer_email}?`)) {
                      voucherMut.mutate({ bookingId: b.id });
                    }
                  }}
                />
                <ResendAccessButton kind="custom" code={b.booking_code} />
              </td>

              <td className="px-3 py-2">
                <select
                  className="h-8 rounded border bg-background px-2 text-xs"
                  value={b.provider_id ?? ""}
                  onChange={(e) => {
                    const pid = e.target.value || null;
                    const p = providers.find((x: any) => x.id === pid);
                    assignMut.mutate({
                      bookingId: b.id,
                      providerId: pid,
                      commissionPct: p?.default_commission_pct ?? b.commission_pct ?? null,
                    });
                  }}
                >
                  <option value="">—</option>
                  {providers.map((p: any) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </td>
              <td className="px-3 py-2">
                <input
                  type="number"
                  className="h-8 w-16 rounded border bg-background px-2 text-xs"
                  defaultValue={b.commission_pct ?? ""}
                  onBlur={(e) => {
                    const val = e.target.value === "" ? null : Number(e.target.value);
                    if (val === (b.commission_pct ?? null)) return;
                    assignMut.mutate({
                      bookingId: b.id,
                      providerId: b.provider_id ?? null,
                      commissionPct: val,
                    });
                  }}
                />
                <span className="ml-1 text-xs text-muted-foreground">%</span>
              </td>
              <td className="space-x-3 px-3 py-2 text-right">
                {b.source === "bokun" && b.status === "paid" && !b.bokun_confirmation_code && (
                  <button
                    className="text-xs font-semibold text-primary hover:underline disabled:opacity-50"
                    disabled={retryMut.isPending}
                    onClick={() => {
                      if (confirm(`¿Reintentar la reserva en Bokun de ${b.booking_code}?`)) {
                        retryMut.mutate({ bookingId: b.id });
                      }
                    }}
                  >
                    Reintentar Bokun
                  </button>
                )}

                {b.status !== "cancelled" && (
                  <button
                    className="text-xs text-destructive hover:underline"
                    onClick={() => {
                      const reason = prompt("Motivo de cancelación (opcional):") ?? "";
                      if (confirm(`¿Cancelar reserva ${b.booking_code}?`)) {
                        cancelMut.mutate({ bookingId: b.id, reason });
                      }
                    }}
                  >
                    Cancelar
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function VoucherCell({
  state,
  pending,
  onResend,
}: {
  state?: { status: string; error: string | null; attempt: number };
  pending: boolean;
  onResend: () => void;
}) {
  const sent = state?.status === "sent";
  return (
    <div className="space-y-1">
      {!state ? (
        <Badge variant="outline">sin enviar</Badge>
      ) : sent ? (
        <Badge variant="default">enviado</Badge>
      ) : (
        <Badge variant="destructive">falló</Badge>
      )}
      {state && !sent && state.error && (
        <div className="max-w-[200px] text-[10px] text-destructive" title={state.error}>
          {state.error.length > 50 ? `${state.error.slice(0, 50)}…` : state.error}
        </div>
      )}
      <button
        className="block text-[11px] font-semibold text-primary hover:underline disabled:opacity-50"
        disabled={pending}
        onClick={onResend}
      >
        {sent ? "Reenviar" : "Enviar voucher"}
      </button>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const variant: "default" | "secondary" | "destructive" | "outline" =
    status === "confirmed" || status === "completed"
      ? "default"
      : status === "paid"
        ? "secondary"
        : status === "failed" || status === "cancelled"
          ? "destructive"
          : "outline";
  return <Badge variant={variant}>{status}</Badge>;
}

function ResendAccessButton({ kind, code }: { kind: "custom" | "bokun"; code: string }) {
  const mut = useMutation({
    mutationFn: () => resendSecureAccess({ data: { kind, code } }),
    onSuccess: (res) =>
      alert(res.ok ? `Acceso seguro enviado a ${res.recipient}. Los enlaces anteriores se revocaron.` : res.error),
    onError: () => alert("No se pudo enviar el acceso seguro."),
  });
  return (
    <Button
      size="sm"
      variant="outline"
      className="mt-1 h-7 text-xs"
      disabled={mut.isPending || !code}
      onClick={() => {
        if (confirm(`¿Reenviar acceso seguro de ${code} al correo confirmado de la reserva?`)) mut.mutate();
      }}
    >
      {mut.isPending ? "Enviando…" : "Reenviar acceso seguro"}
    </Button>
  );
}

function TestAccessResend() {
  const [code, setCode] = useState("");
  return (
    <div className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border bg-card p-3 text-sm">
      <span className="text-muted-foreground">Reserva Bókun (folio MOS-…):</span>
      <Input className="h-8 max-w-[12rem]" value={code} onChange={(e) => setCode(e.target.value.trim().toUpperCase())} placeholder="MOS-000000" />
      {/^[A-Z]{2,5}-\d{3,12}$/.test(code) && <ResendAccessButton kind="bokun" code={code} />}
    </div>
  );
}
